import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { useIdCards } from './utils/useIdCards';
import { DEFAULT_PAGINATION_MODEL, getIdCardsColumns } from './utils/columns';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import pallet from '../../layouts/pallet';
import IdCardsFilters from './components/IdCardsFilters';
import IdCardsActions from './components/IdCardsActions';
import {
  FiltersContainer,
  GridContainer,
  PageContainer,
  SearchButton,
  SearchButtonBox,
  StyledLinearProgress
} from './styled';
import { dataGridSx } from '../common/styled/DataGrid';
import Icons from '../../components/icons';
import tableColumnUtils from '../../utils/tableColumnUtils';
import { PaperWrapper, Title } from '../common/styled';

const IdCardsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.profile);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [viewLoading, setViewLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  const [hasSearched, setHasSearched] = useState(false);
  const handleCloseToast = () => setToastOpen(false);
  const isActionEnabled = selectedIds.length > 0;

  const {
    academicYear,
    setAcademicYear,
    grade,
    setGrade,
    division,
    setDivision,
    typeOfRelation,
    setTypeOfRelation,
    name,
    setName,
    rows,
    loading,
    canSearch,
    search,
    generatePdf,
    gradeOptions,
    divisionOptions,
    getIdCardsUrl
  } = useIdCards({ showToast, school: user?.school });

  const handleSearch = () => {
    if (!user?.school) return;
    search(user.school);
    setHasSearched(true);
    setSelectedIds([]);
  };

  const handleGeneratePdf = async () => {
    if (!user?.school) return;
    setGenerateLoading(true);
    await generatePdf(user.school, selectedIds as any);
    setGenerateLoading(false);
  };

  const handleViewIdCards = async () => {
    if (!user?.school) return;
    setViewLoading(true);
    const url = getIdCardsUrl({
      academicYear,
      school: user.school,
      grade,
      division,
      typeOfRelation,
      name,
      selectedIds
    });
    window.open(url, '_blank');
    setViewLoading(false);
  };

  const allRowsSelected = rows.length > 0 && selectedIds.length === rows.length;
  const isSomeRowsSelected =
    selectedIds.length > 0 && selectedIds.length < rows.length;

  const onHeaderCheckboxClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelectedIds(rows.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const onCellCheckboxClick = (id: string) => {
    setSelectedIds((prevSelectedRows) =>
      prevSelectedRows.includes(id)
        ? prevSelectedRows.filter((rowId) => rowId !== id)
        : [...prevSelectedRows, id]
    );
  };

  const idCardsColumns: GridColDef[] = getIdCardsColumns({
    selectedIds,
    onHeaderCheckboxClick,
    onCellCheckboxClick,
    allRowsSelected,
    isSomeRowsSelected,
    relationType: typeOfRelation as any
  }) as GridColDef[];

  const [isWrapped, setIsWrapped] = useState(false);
  const containerRef = useRef<any>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      const items = Array.from(container.children) as HTMLElement[];
      if (items.length === 0) return;

      const firstRowTop = items[0].offsetTop;

      const isWrapped = items.some((item) => item.offsetTop > firstRowTop + 25);

      setIsWrapped(isWrapped);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <PageContainer>
      <Title>Photo ID Cards</Title>
      <PaperWrapper>
        <FiltersContainer ref={containerRef}>
          <IdCardsFilters
            academicYear={academicYear}
            setAcademicYear={setAcademicYear}
            grade={grade}
            setGrade={setGrade}
            division={division}
            setDivision={setDivision}
            typeOfRelation={typeOfRelation}
            setTypeOfRelation={setTypeOfRelation}
            name={name}
            setName={setName}
            gradeOptions={gradeOptions}
            divisionOptions={divisionOptions}
            setHasSearched={setHasSearched}
          />
          <SearchButtonBox isWrapped={isWrapped}>
            <SearchButton
              variant='contained'
              onClick={handleSearch}
              disabled={!canSearch}
              loading={loading}
            >
              <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
              Search
            </SearchButton>
          </SearchButtonBox>
        </FiltersContainer>
      </PaperWrapper>
      {hasSearched && (
        <PaperWrapper>
          {loading && <StyledLinearProgress />}
          <GridContainer>
            <IdCardsActions
              isActionEnabled={isActionEnabled}
              viewLoading={viewLoading}
              generateLoading={generateLoading}
              onViewIdCards={handleViewIdCards}
              onGeneratePdf={handleGeneratePdf}
            />
            <DataGrid
              autoHeight
              rows={rows}
              getRowId={(row) => row.id || row.enrollmentNumber}
              columns={idCardsColumns}
              paginationModel={DEFAULT_PAGINATION_MODEL}
              pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
              loading={loading}
              sx={dataGridSx(pallet)}
              disableRowSelectionOnClick
            />
          </GridContainer>
        </PaperWrapper>
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </PageContainer>
  );
};

export default IdCardsPage;
